// @flow

import React, {Fragment} from 'react';
import {connect} from 'react-redux';
import cs from 'classnames';
import {Link, browserHistory} from 'react-router';

import {gt} from 'app/utils/i18n';
import {SPPage} from 'app/general/structures/SPPage';
import {MainContentContainer} from 'app/general/structures/MainContentContainer';
import {get} from 'app/utils/request';
import {USERDATA_ACCOUNTS_API_URL} from 'app/urls';
import {StyledTitleH1} from 'app/general/structures/SPPage';
import {
  ACCOUNT_OVERVIEW_URL,
  HOME_REDIRECT,
  ACCOUNT_SWITCH_URL,
} from 'app/urls';
import {requestLogout} from 'app/current-user/api';
import {switchAccount} from 'app/general/actions';
import {type WithRouterProps} from 'app/general/withTypedRouter';
import {getContentType, type contentType} from 'app/utils/content-type';
import {type StoreShapeType} from 'app/reducers/types';
import {LazyTooltip} from 'app/components/general/LazyTooltip';

type AccountInfoType = {
  account_id: number,
  account_name: string,
  company_logo: null | string,
  is_current: boolean,
  is_saml: boolean,
  is_guest_until: string,
  login_restricted: boolean,
  login_url?: string,
};

type OwnProps = {|
  ...WithRouterProps<{next?: string, ct?: contentType}>,
|};

type StateProps = {||};

type DispatchProps = {|
  requestLogout: () => void,
  switchAccount: (accountId: number, targetURL: string) => Promise<any>,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

type State = {|
  status: 'loading' | 'idle' | 'switching',
  accounts: Array<AccountInfoType>,
|};

class BaseSwitchAccountPage extends React.Component<Props, State> {
  state = {
    status: 'loading',
    accounts: [],
  };

  componentDidMount() {
    get(USERDATA_ACCOUNTS_API_URL).then((accounts: Array<AccountInfoType>) =>
      this.setState({accounts, status: 'idle'})
    );
  }

  getCurrentAccount() {
    for (let i = 0; i < this.state.accounts.length; i += 1) {
      if (this.state.accounts[i].is_current) {
        return this.state.accounts[i];
      }
    }

    return null;
  }

  switchAccount = (account_id: number) => {
    const currentAccount = this.getCurrentAccount();

    if (currentAccount != null && currentAccount.login_restricted) {
      // User is restricted from loging to that account - he is on a saml account
      // currently. Log him out and redirect to login page.
      this.props.requestLogout();
      return;
    }

    const {
      location: {
        query: {next},
      },
    } = this.props;
    const targetURL = next != null ? next : HOME_REDIRECT;

    this.setState({status: 'switching'});
    this.props
      .switchAccount(account_id, targetURL)
      .catch(() => this.setState({status: 'idle'}));
  };

  render() {
    const {
      requestLogout,
      location: {
        query: {ct, next},
      },
    } = this.props;
    const {status, accounts} = this.state;

    const currentAccount = this.getCurrentAccount();

    let currentAccountLoginRestricted = false;

    if (currentAccount != null && currentAccount.login_restricted) {
      currentAccountLoginRestricted = true;
    }

    return (
      <SPPage pageTitle={gt('Account Switch')} horizontalPadding={false}>
        <MainContentContainer>
          <div className="row">
            <div className="col-12 text-center">
              <StyledTitleH1 className="justify-content-center">
                {gt('Account Switch')}
              </StyledTitleH1>
              {ct != null && next != null ? (
                <Fragment>
                  <h2 className="lead">
                    {gt(
                      'That %s is not available on this account.',
                      getContentType(ct).name
                    )}
                  </h2>
                  <h2 className="lead">
                    {gt('Select an account to switch to below.')}
                  </h2>
                </Fragment>
              ) : (
                <h2 className="lead">
                  {gt('Choose the SweetProcess account you wish to use today')}
                </h2>
              )}
              <div className="row justify-content-center">
                <div className="col-12 col-md-8 col-lg-6">
                  {status === 'loading' || status === 'switching' ? (
                    <div className="border-top border-bottom my-4 py-3">
                      <i className="loading fa fa-circle-notch fa-spin" />{' '}
                      {status === 'loading'
                        ? gt('Loading accounts...')
                        : gt('Switching account...')}
                    </div>
                  ) : (
                    <div className="list-group my-4 text-left">
                      {accounts.map((account) => (
                        <AccountButton
                          key={account.account_id}
                          account={account}
                          currentAccountLoginRestricted={
                            currentAccountLoginRestricted
                          }
                          switchAccount={this.switchAccount}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <footer>
                <Link className="btn btn-link" to={ACCOUNT_OVERVIEW_URL}>
                  {gt('My Account')}
                </Link>
                <button
                  type="submit"
                  className="btn-link btn"
                  onClick={() => requestLogout()}
                >
                  {gt("Logout")}
                </button>
              </footer>
            </div>
          </div>
        </MainContentContainer>
      </SPPage>
    );
  }
}

export const SwitchAccountPage = connect<
  Props,
  OwnProps,
  StateProps,
  DispatchProps,
  StoreShapeType,
  _
>(
  // $FlowFixMe
  null,
  // $FlowFixMe
  {requestLogout, switchAccount}
)(BaseSwitchAccountPage);

export const navigateToSwitchAccountPage = (ct: contentType) =>
  browserHistory.push(
    `${ACCOUNT_SWITCH_URL}?next=${window.location.pathname}&ct=${ct}`
  );

type AccountButtonProps = {
  account: AccountInfoType,
  currentAccountLoginRestricted: boolean,
  switchAccount: (number) => void,
};

const AccountButton = ({
  account,
  currentAccountLoginRestricted,
  switchAccount,
}: AccountButtonProps) => {
  return account.is_saml && account.login_restricted ? (
    <a
      href={account.login_url}
      className={cs(
        'list-group-item list-group-item-action',
        'd-flex justify-content-between align-items-center',
        {disabled: account.is_current}
      )}
      key={account.account_id}
    >
      <span>{account.account_name}</span>
      {account.is_current ? (
        <span className="badge badge-primary">{gt('current')}</span>
      ) : null}
      {account.is_saml ? (
        <span className="mr-1 badge badge-success">
          <i className="far fa-sign-in"></i> {gt("SSO enabled")}
        </span>
      ) : null}
      <span>
        <i className="fas fa-lock"></i>
      </span>
    </a>
  ) : account.is_guest_until != null ? (
    <LazyTooltip
      key={account.account_id}
      tooltip={
        <span className="small">
          {gt(
            "Can't login (nor switch accounts) to an account if you are a guest"
          )}
        </span>
      }
    >
      <button
        className={cs(
          'list-group-item list-group-item-action',
          'd-flex justify-content-between align-items-center'
        )}
      >
        <span>{account.account_name}</span>
        <span className="mr-1 badge badge-success">
          <i className="far fa-sign-in"></i> {gt("Guest Invitation")}
        </span>
        <span>
          <i className="fas fa-lock"></i>
        </span>
      </button>
    </LazyTooltip>
  ) : (
    <button
      title={
        currentAccountLoginRestricted
          ? gt('You will be logged out and redirected to the login page')
          : null
      }
      onClick={() => switchAccount(account.account_id)}
      className={cs(
        'list-group-item list-group-item-action',
        'd-flex justify-content-between align-items-center',
        {disabled: account.is_current}
      )}
      key={account.account_id}
    >
      <span>{account.account_name}</span>
      {account.is_current ? (
        <span className="badge badge-primary">{gt('current')}</span>
      ) : null}
      {account.is_saml ? (
        <span className="mr-1 badge badge-success">
          <i className="far fa-sign-in"></i> {gt("SSO enabled")}
        </span>
      ) : null}
      {currentAccountLoginRestricted && !account.is_current ? (
        <span>
          <i className="fas fa-lock"></i> {gt('Logout')}
        </span>
      ) : null}
    </button>
  );
};
